import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Offering, OfferingDocument } from '@arrived/models';
import { useOfferingDocumentsQuery } from '@arrived/queries';

import { DataStateHandler } from '../../../DataStateHandler';
import { PROPERTY_HASH_MAP } from '../../Property.constants';
import { PropertyDetailsSection } from '../PropertyDetailsSection';
import { PropertyDetailsFinancials } from './PropertyDetailsFinancials';

import { filterPropertyFinancialDocuments } from '../utils/filterPropertyFinancialDocuments';

interface PropertyDetailsFinancialsSectionProps {
  offering: Offering;
  offeringDocuments: OfferingDocument[];
}

export const PropertyDetailsFinancialsSection = ({
  offering,
}: Omit<PropertyDetailsFinancialsSectionProps, 'offeringDocuments'>) => {
  const offeringDocumentsState = useOfferingDocumentsQuery(offering.id);

  return (
    <DataStateHandler queries={[offeringDocumentsState]}>
      {(offeringDocumentsStateSuccess) => (
        <PropertyDetailsFinancialsSectionContent
          offering={offering}
          offeringDocuments={offeringDocumentsStateSuccess.data}
        />
      )}
    </DataStateHandler>
  );
};

const PropertyDetailsFinancialsSectionContent = ({
  offering,
  offeringDocuments,
}: PropertyDetailsFinancialsSectionProps) => {
  const financialDocuments = useMemo(() => filterPropertyFinancialDocuments(offeringDocuments), [offeringDocuments]);
  if (offering.isFund || !financialDocuments.length) return null;

  return (
    <PropertyDetailsSection
      areSectionsColumns
      hash={PROPERTY_HASH_MAP.financialBreakdown}
      title={
        <FormattedMessage
          id="property-details-property-financial-breakdown-title"
          defaultMessage="Property Financial Breakdown"
        />
      }
      description={
        <FormattedMessage
          id="property-details-property-financial-breakdown-description"
          defaultMessage="The Property Financial Breakdown outlines the operating performance of the property during each dividend period. The breakdown can be used to understand the financial statements for the property and the cash flow that’s being distributed to investors as dividends."
        />
      }
    >
      <PropertyDetailsFinancials financialDocuments={financialDocuments} />
    </PropertyDetailsSection>
  );
};
